import React from "react";
import { Link } from "react-router-dom";
import { LegalLayout, Section, Field } from "../components/legal/LegalLayout";
import { legalConfig } from "../data/legalConfig";

export default function PolitiqueConfidentialite() {
  return (
    <LegalLayout
      title="Politique de confidentialité"
      description="Politique de confidentialité de Louis-Melchior Danse : données collectées, finalité, base légale, durée de conservation, droits RGPD."
      testId="page-confidentialite"
      breadcrumbPath="/confidentialite"
    >
      <p className="text-base mb-8">
        La présente politique décrit la manière dont {legalConfig.brandName} (ci-après « le responsable du traitement ») collecte, traite et protège les données personnelles des utilisateurs du site {legalConfig.domain}, conformément au Règlement Général sur la Protection des Données (RGPD — Règlement (UE) 2016/679) et à la loi française n°78-17 du 6 janvier 1978 modifiée.
      </p>

      <Section title="Responsable du traitement" testId="pc-responsable">
        <p>
          {legalConfig.publisherFullName}, exerçant sous le nom {legalConfig.brandName}, en qualité d'entrepreneur individuel (SIREN&nbsp;{legalConfig.siren}).
        </p>
        <p>
          Adresse : {legalConfig.legalAddress.line1}, {legalConfig.legalAddress.postalCode} {legalConfig.legalAddress.city}, {legalConfig.legalAddress.country}.<br />
          Contact :{" "}
          <a href={`mailto:${legalConfig.contactEmail}`} className="text-[#C9A869] hover:text-[#DCC28E] underline underline-offset-4">
            {legalConfig.contactEmail}
          </a>.
        </p>
      </Section>

      <Section title="Données collectées" testId="pc-donnees">
        <p><strong className="text-[#C9A869]">Via le formulaire de contact :</strong></p>
        <ul className="list-disc list-outside pl-5 space-y-1.5 marker:text-[#C9A869]">
          <li>nom et prénom ;</li>
          <li>adresse email ;</li>
          <li>numéro de téléphone (si renseigné) ;</li>
          <li>type de cours demandé ;</li>
          <li>lieu approximatif ;</li>
          <li>contenu du message libre ;</li>
          <li>code promotionnel éventuel.</li>
        </ul>
        <p className="mt-3"><strong className="text-[#C9A869]">Via l'assistant conversationnel :</strong> les questions posées et les réponses générées, associées à un identifiant de session anonyme.</p>
        <p><strong className="text-[#C9A869]">Données techniques strictement nécessaires :</strong> adresse IP (transmise temporairement par votre navigateur, non stockée à des fins de profilage), informations de session pour l'affichage du site.</p>
      </Section>

      <Section title="Finalités et bases légales" testId="pc-finalites">
        <p><strong className="text-[#C9A869]">Répondre aux demandes de contact et organiser un cours :</strong> base légale = mesures précontractuelles à la demande de la personne concernée (art. 6.1.b RGPD).</p>
        <p><strong className="text-[#C9A869]">Assistant conversationnel :</strong> base légale = intérêt légitime à fournir une aide informative (art. 6.1.f RGPD).</p>
        <p><strong className="text-[#C9A869]">Conservation des justificatifs comptables (factures) :</strong> base légale = obligation légale (art. 6.1.c RGPD — Code de commerce, article L.123-22).</p>
      </Section>

      <Section title="Durées de conservation" testId="pc-duree">
        <ul className="list-disc list-outside pl-5 space-y-1.5 marker:text-[#C9A869]">
          <li>Demandes de contact non suivies d'un cours : <strong>3 ans</strong> après le dernier échange.</li>
          <li>Données clients (cours réalisés) : <strong>3 ans</strong> après la fin de la relation commerciale, à des fins de prospection.</li>
          <li>Pièces comptables (factures, devis acceptés) : <strong>10 ans</strong> (obligation Code de commerce).</li>
          <li>Conversations avec l'assistant : <strong>90 jours</strong> maximum à des fins d'amélioration du service.</li>
        </ul>
      </Section>

      <Section title="Destinataires et sous-traitants" testId="pc-destinataires">
        <p>
          Les données sont destinées exclusivement à {legalConfig.brandName}. Elles ne sont jamais vendues ni cédées à des tiers à des fins commerciales. Elles peuvent transiter par les sous-traitants techniques suivants, indispensables au fonctionnement du site :
        </p>
        <Field label="Hébergeur" value={legalConfig.technicalProviders.hosting} />
        <Field label="Base de données" value={legalConfig.technicalProviders.database} />
        <Field label="Envoi d'emails" value={legalConfig.technicalProviders.emailDelivery} />
        <Field label="Assistant conversationnel" value={legalConfig.technicalProviders.aiAssistant} />
      </Section>

      <Section title="Transferts hors Union européenne" testId="pc-transferts">
        <p>
          Certains sous-traitants techniques (Resend, OpenAI) peuvent traiter des données aux États-Unis. Ces transferts sont encadrés par les clauses contractuelles types de la Commission européenne et, le cas échéant, par la décision d'adéquation Data Privacy Framework. Aucune donnée sensible n'est transférée.
        </p>
      </Section>

      <Section title="Vos droits" testId="pc-droits">
        <p>Conformément aux articles 15 à 22 du RGPD, vous disposez des droits suivants&nbsp;:</p>
        <ul className="list-disc list-outside pl-5 space-y-1.5 marker:text-[#C9A869]">
          <li>droit d'accès à vos données ;</li>
          <li>droit de rectification ;</li>
          <li>droit à l'effacement (« droit à l'oubli ») ;</li>
          <li>droit à la limitation du traitement ;</li>
          <li>droit à la portabilité ;</li>
          <li>droit d'opposition ;</li>
          <li>droit de définir des directives relatives au sort de vos données après votre décès ;</li>
          <li>droit d'introduire une réclamation auprès de la CNIL (<a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-[#C9A869] hover:text-[#DCC28E] underline underline-offset-4">www.cnil.fr</a>).</li>
        </ul>
        <p className="mt-3">
          Pour exercer vos droits, écrivez à&nbsp;:&nbsp;
          <a href={`mailto:${legalConfig.contactEmail}`} className="text-[#C9A869] hover:text-[#DCC28E] underline underline-offset-4">
            {legalConfig.contactEmail}
          </a>. Une réponse vous sera apportée dans un délai d'un mois.
        </p>
      </Section>

      <Section title="Sécurité" testId="pc-securite">
        <p>
          Le site est servi en HTTPS (TLS). Les données sont stockées sur des serveurs situés en Europe lorsque cela est techniquement possible. L'accès aux données est restreint au seul responsable du traitement. Les clés et secrets d'API sont stockés via des variables d'environnement et ne sont jamais exposés côté client.
        </p>
      </Section>

      <Section title="Cookies" testId="pc-cookies">
        <p>
          À ce jour, {legalConfig.domain} n'utilise que des cookies strictement nécessaires à son fonctionnement (mémorisation de l'état de l'assistant et de la bannière promotionnelle dans la session du navigateur). Aucun cookie de traçage publicitaire, d'analyse d'audience tiers ou de profilage n'est déposé. Aucun consentement n'est donc requis à ce stade.
        </p>
        <p>
          Si des outils d'analyse (par ex. Google Analytics, Microsoft Clarity) ou de publicité (Google Ads) sont activés ultérieurement, une bannière de consentement conforme à la délibération CNIL 2020-091 sera mise en place. Les traceurs seront alors bloqués avant recueil du consentement.
        </p>
      </Section>

      <p className="mt-10 text-sm text-[#F7F3EA]/60">
        Voir aussi :{" "}
        <Link to="/mentions-legales" className="text-[#C9A869] hover:text-[#DCC28E] underline underline-offset-4">Mentions légales</Link>
        {" · "}
        <Link to="/conditions-generales" className="text-[#C9A869] hover:text-[#DCC28E] underline underline-offset-4">Conditions générales</Link>
      </p>
    </LegalLayout>
  );
}
